import type { SemanticDiff, SemanticDiffSegment } from "./api.ts";
import { messages } from "./i18n.ts";

/**
 * The whole document, with what changed marked where it falls.
 *
 * Not a separate pane and not a list of changes. A procedure only means
 * anything in context: "the CTO is accountable" becoming "the COO is
 * accountable" matters because of the paragraph around it, and a reader asked
 * to demonstrate understanding needs that paragraph. So every row is rendered,
 * unchanged ones included, and the edits are marked in place.
 *
 * The toolbar lives in the module view instead of here, because the choice
 * between this and the clean procedure belongs to the page rather than to the
 * comparison.
 */

function DiffText({ segments }: { segments: SemanticDiffSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        const key = `${index}-${segment.kind}-${segment.text.slice(0, 12)}`;
        if (segment.kind === "removed") {
          return (
            <del className="diff-word-removed" key={key}>
              {segment.text}
            </del>
          );
        }
        if (segment.kind === "added") {
          return (
            <ins className="diff-word-added" key={key}>
              {segment.text}
            </ins>
          );
        }
        return <span key={key}>{segment.text}</span>;
      })}
    </>
  );
}

export function DocumentDiffViewer({ diff }: { diff: SemanticDiff }) {
  let lastSection: string | null = null;
  // Numbered across changed rows only, so "next edit" steps between edits
  // rather than between paragraphs.
  let editIndex = -1;

  return (
    <section
      aria-label={messages.module.comparison}
      className="document-diff-document"
      data-testid="revision-diff"
    >
      {diff.rows.map((row) => {
        const showSection = row.section !== null && row.section !== lastSection;
        lastSection = row.section;
        const changed = row.kind !== "same";
        if (changed) editIndex += 1;
        return (
          <div key={row.id}>
            {showSection ? <h3 className="document-diff-section">{row.section}</h3> : null}
            <p
              className={`document-diff-inline document-diff-${row.kind}`}
              data-edit-index={changed ? editIndex : undefined}
            >
              <DiffText segments={row.inline} />
            </p>
          </div>
        );
      })}
    </section>
  );
}
